import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Card,
  CardContent,
  Collapse,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';

import CashDeskSubmissionDialog from '../dialogs/CashDeskSubmissionDialog';
import ManageCashDesksDialog from '../dialogs/ManageCashDesksDialog';
import {
  getCashDesks,
  getCashDeskSubmissions,
  getCashDeskMetrics,
  deleteCashDeskSubmission,
} from '../../services/cashflowService';

const PAYMENT_TYPE_LABELS = {
  cash: 'מזומן',
  check: "צ'ק",
  credit_card: 'אשראי',
  bank_transfer: 'העברה',
  other: 'אחר',
};

const CashDeskTabContent = () => {
  // Filter state
  const [selectedCashDesk, setSelectedCashDesk] = useState('all');
  const [periodTypeFilter, setPeriodTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  // Dialog state
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [metricsExpanded, setMetricsExpanded] = useState(true);

  // Delete state
  const [deleteError, setDeleteError] = useState('');

  const queryClient = useQueryClient();

  // Queries
  const { data: cashDesks = [], refetch: refetchCashDesks } = useQuery({
    queryKey: ['cash-desks'],
    queryFn: () => getCashDesks(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: submissionsData, isLoading: isLoadingSubmissions } = useQuery({
    queryKey: ['cashdesk-submissions', selectedCashDesk, periodTypeFilter, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => getCashDeskSubmissions({
      cash_desk_id: selectedCashDesk !== 'all' ? selectedCashDesk : undefined,
      period_type: periodTypeFilter !== 'all' ? periodTypeFilter : undefined,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    }),
    staleTime: 30 * 1000,
  });

  const { data: metricsData } = useQuery({
    queryKey: ['cashdesk-metrics', selectedCashDesk, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => getCashDeskMetrics({
      cash_desk_id: selectedCashDesk !== 'all' ? selectedCashDesk : undefined,
      start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
      end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    }),
    staleTime: 60 * 1000,
  });

  const submissions = submissionsData?.items || [];
  const summary = submissionsData?.summary || {};
  const metrics = metricsData || {};

  const handleDelete = async (submissionId) => {
    setDeleteError('');
    try {
      await deleteCashDeskSubmission(submissionId);
      queryClient.refetchQueries({ queryKey: ['cashdesk-submissions'] });
      queryClient.refetchQueries({ queryKey: ['cashdesk-metrics'] });
      queryClient.refetchQueries({ queryKey: ['cashflow'] });
      queryClient.refetchQueries({ queryKey: ['cashflow-summary'] });
      queryClient.refetchQueries({ queryKey: ['general-transactions'] });
    } catch (err) {
      setDeleteError(err?.response?.data?.error || 'שגיאה במחיקת דיווח');
    }
  };

  // Chart data for day of week
  const dayOfWeekChartData = {
    labels: (metrics.by_day_of_week || []).map(d => d.day_name),
    datasets: [
      {
        label: 'ממוצע הכנסה',
        data: (metrics.by_day_of_week || []).map(d => d.avg_income),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
      {
        label: 'ממוצע הוצאות עובדים',
        data: (metrics.by_day_of_week || []).map(d => d.avg_employee_expenses || 0),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
      },
    ],
  };

  // Chart data for monthly
  const monthlyChartData = {
    labels: (metrics.by_month || []).map(m => m.month),
    datasets: [
      {
        label: 'סה"כ הכנסה',
        data: (metrics.by_month || []).map(m => m.total_income),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
      {
        label: 'סה"כ הוצאות עובדים',
        data: (metrics.by_month || []).map(m => m.total_employee_expenses || 0),
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  return (
    <Box>
      {/* Actions & Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <DatePicker
          label="חודש"
          value={startDate}
          onChange={(val) => {
            if (val) {
              setStartDate(startOfMonth(val));
              setEndDate(endOfMonth(val));
            }
          }}
          views={['month', 'year']}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />
        <DatePicker
          label="מתאריך"
          value={startDate}
          onChange={(val) => setStartDate(val)}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />
        <DatePicker
          label="עד תאריך"
          value={endDate}
          onChange={(val) => setEndDate(val)}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>קופה</InputLabel>
          <Select
            value={selectedCashDesk}
            onChange={(e) => setSelectedCashDesk(e.target.value)}
            label="קופה"
          >
            <MenuItem value="all">הכל</MenuItem>
            {cashDesks.map((cd) => (
              <MenuItem key={cd.CashDeskID} value={cd.CashDeskID}>
                {cd.Name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>סוג תקופה</InputLabel>
          <Select
            value={periodTypeFilter}
            onChange={(e) => setPeriodTypeFilter(e.target.value)}
            label="סוג תקופה"
          >
            <MenuItem value="all">הכל</MenuItem>
            <MenuItem value="daily">יומי</MenuItem>
            <MenuItem value="monthly">חודשי</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowManageDialog(true)}
          sx={{ direction: 'ltr' }}
        >
          ניהול קופות
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowSubmissionDialog(true)}
          disabled={cashDesks.filter(cd => cd.IsActive).length === 0}
          sx={{ direction: 'ltr' }}
        >
          הוסף דיווח
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ minWidth: 140 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                סה"כ הכנסות מתוך קופה
              </Typography>
              <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1.5rem' }}>
                ₪{(summary.total_income || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ minWidth: 140 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: '1.1rem', mb: 0.5 }}>
                % הוצאות עובדים מתוך קופה
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                {summary.avg_employee_percent != null ? `${summary.avg_employee_percent}%` : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Metrics Section */}
      {metrics.by_day_of_week?.length > 0 && (
        <Paper sx={{ mb: 2, p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: metricsExpanded ? 2 : 0 }}
            onClick={() => setMetricsExpanded(!metricsExpanded)}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              מטריקות
            </Typography>
            {metricsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={metricsExpanded}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>ממוצע לפי יום בשבוע</Typography>
                <Bar data={dayOfWeekChartData} options={chartOptions} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>סה"כ לפי חודש</Typography>
                <Bar data={monthlyChartData} options={chartOptions} />
              </Grid>
            </Grid>

            {/* Day of week % table */}
            {metrics.by_day_of_week?.some(d => d.avg_employee_percent != null) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>% הוצאות עובדים מתוך קופה לפי יום</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(metrics.by_day_of_week || []).map((d) => (
                    <Chip
                      key={d.day}
                      label={`${d.day_name}: ${d.avg_employee_percent != null ? d.avg_employee_percent + '%' : '-'}`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Collapse>
        </Paper>
      )}

      {/* Error */}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
          {deleteError}
        </Alert>
      )}

      {/* Submissions Table */}
      <Paper>
      <TableContainer>
        {isLoadingSubmissions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : submissions.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">
              אין דיווחים בטווח התאריכים הנבחר
            </Typography>
          </Box>
        ) : (
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.200' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>תאריך</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>קופה</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>סוג</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>סכום</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>הוצאות עובדים</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>%</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>אמצעי תשלום</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>הערה</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((sub) => (
                <TableRow key={sub.SubmissionID} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                  <TableCell align="center" sx={{ fontSize: '0.95rem' }}>{sub.PeriodDisplay}</TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.95rem' }}>{sub.CashDeskName}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={sub.PeriodTypeDisplay}
                      size="small"
                      variant="outlined"
                      color={sub.PeriodType === 'daily' ? 'primary' : 'secondary'}
                      sx={{ fontSize: '0.85rem', fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'success.main' }}>
                    ₪{parseFloat(sub.TotalAmount).toLocaleString('he-IL', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                    {sub.EmployeeExpenses != null
                      ? `₪${parseFloat(sub.EmployeeExpenses).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`
                      : '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                    {sub.EmployeeExpensePercent != null
                      ? `${sub.EmployeeExpensePercent}%`
                      : '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.95rem' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {(sub.PaymentMethods || []).map((pm, i) => (
                        <Chip
                          key={i}
                          label={`${PAYMENT_TYPE_LABELS[pm.Type] || pm.Type} ₪${parseFloat(pm.Amount).toLocaleString('he-IL', { minimumFractionDigits: 2 })}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.95rem', maxWidth: 200 }}>
                    {sub.Note ? (
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 180,
                        }}
                      >
                        {sub.Note}
                      </Typography>
                    ) : ''}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(sub.SubmissionID)}
                      title="מחק דיווח"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      </Paper>

      {/* Dialogs */}
      <CashDeskSubmissionDialog
        open={showSubmissionDialog}
        onClose={() => setShowSubmissionDialog(false)}
        cashDesks={cashDesks}
      />
      <ManageCashDesksDialog
        open={showManageDialog}
        onClose={() => setShowManageDialog(false)}
        cashDesks={cashDesks}
        refetchCashDesks={refetchCashDesks}
      />
    </Box>
  );
};

export default CashDeskTabContent;
